import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateCarrierService } from "@/api/carrier-services";
import axios from "axios";
import { toast } from "sonner";

interface AddCarrierServiceDialogProps {
  open: boolean;
  onClose: () => void;
  carrierId: string;
  carrierName: string;
}

interface FormState {
  code: string;
  name: string;
  estimatedDays: string;
  dimFactorCm: string;
  dimFactorInch: string;
}

const emptyForm: FormState = {
  code: "",
  name: "",
  estimatedDays: "",
  dimFactorCm: "",
  dimFactorInch: "",
};

export function AddCarrierServiceDialog({
  open,
  onClose,
  carrierId,
  carrierName,
}: AddCarrierServiceDialogProps) {
  const [form, setForm] = useState<FormState>(emptyForm);

  const createMutation = useCreateCarrierService({
    onSuccess: (data) => {
      toast.success("Service created", {
        description: `${data.code} added to ${carrierName}`,
      });
      setForm(emptyForm);
      onClose();
    },
    onError: (err) => {
      let description = err.message;
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as
          | { error?: string; details?: { message?: string } }
          | undefined;
        if (data?.error === "DUPLICATE_CODE" && data.details?.message) {
          description = data.details.message;
        }
      }
      toast.error("Failed to create service", { description });
    },
  });

  const submit = () => {
    if (!form.code.trim() || !form.name.trim()) return;
    createMutation.mutate({
      carrierId,
      input: {
        code: form.code.trim(),
        name: form.name.trim(),
        estimatedDays: form.estimatedDays ? Number(form.estimatedDays) : null,
        dimFactorCm: form.dimFactorCm ? Number(form.dimFactorCm) : null,
        dimFactorInch: form.dimFactorInch ? Number(form.dimFactorInch) : null,
      },
    });
  };

  const isValid = form.code.trim() && form.name.trim();

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setForm(emptyForm);
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add service to {carrierName}</DialogTitle>
          <DialogDescription>
            Manually create a CarrierService row. For external carriers, prefer
            "Discover services" to populate from the provider API.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="cs-code">Code *</Label>
            <Input
              id="cs-code"
              value={form.code}
              onChange={(e) =>
                setForm({
                  ...form,
                  // Match server convention: lowercase letters/digits/underscores
                  // only (e.g. purolator_express, ups_ground).
                  code: e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9_]/g, "_"),
                })
              }
              placeholder="ups_ground"
              autoFocus
            />
            <p className="text-xs text-muted-foreground mt-1">
              Lowercase letters, digits, and underscores. Unique per carrier.
            </p>
          </div>
          <div>
            <Label htmlFor="cs-name">Name *</Label>
            <Input
              id="cs-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ground Service"
            />
          </div>
          <div>
            <Label htmlFor="cs-eta">ETA days</Label>
            <Input
              id="cs-eta"
              type="number"
              min={0}
              value={form.estimatedDays}
              onChange={(e) =>
                setForm({ ...form, estimatedDays: e.target.value })
              }
              placeholder="5"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cs-dim-cm">DIM factor (cm³/kg)</Label>
              <Input
                id="cs-dim-cm"
                type="number"
                min={1}
                step="0.01"
                value={form.dimFactorCm}
                onChange={(e) =>
                  setForm({ ...form, dimFactorCm: e.target.value })
                }
                placeholder="5000"
              />
            </div>
            <div>
              <Label htmlFor="cs-dim-in">DIM factor (in³/lb)</Label>
              <Input
                id="cs-dim-in"
                type="number"
                min={1}
                step="0.01"
                value={form.dimFactorInch}
                onChange={(e) =>
                  setForm({ ...form, dimFactorInch: e.target.value })
                }
                placeholder="139"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Divide L × W × H by this to get billable dim weight. Common values:
            5000 / 6000 (cm³/kg), 139 / 166 (in³/lb). Leave empty if the carrier
            API already returns dim weight or DIM doesn't apply.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!isValid || createMutation.isPending}
          >
            {createMutation.isPending ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
